import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Circle, CircleMarker, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getGeoProspects } from "../../../api";

const pin = L.divIcon({ className: "", iconSize: [22, 22], iconAnchor: [11, 22], html: '<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;background:#1e50ff;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35)"></div>' });

export const STATUS_COLORS = {
  nouveau: { c: "#64748b", l: "Nouveau" },
  contacte: { c: "#f59e0b", l: "Contacté" },
  relance: { c: "#a855f7", l: "Relancé" },
  converti: { c: "#16a34a", l: "Converti" },
  perdu: { c: "#dc2626", l: "Perdu" },
};
const colorOf = (status) => (STATUS_COLORS[status] || STATUS_COLORS.nouveau).c;

const FitRadius = ({ center, radiusKm }) => {
  const map = useMap();
  useEffect(() => {
    map.fitBounds(L.latLng(center).toBounds(radiusKm * 2000), { padding: [10, 10] });
  }, [map, center, radiusKm]);
  return null;
};

const ProspectDots = ({ prospects }) => prospects.map((p) => (
  <CircleMarker key={p.id} center={[p.lat, p.lng]} radius={5} pathOptions={{ color: "#fff", fillColor: colorOf(p.status), fillOpacity: 0.95, weight: 1.5 }}>
    <Tooltip>{p.societe} · {p.ville} · {(STATUS_COLORS[p.status] || STATUS_COLORS.nouveau).l}{p.last_contacted ? ` · contacté le ${p.last_contacted.slice(0, 10)}` : ""}</Tooltip>
  </CircleMarker>
));

const Legend = ({ prospects, show, setShow }) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-600 mb-3" data-testid="prospect-map-legend">
    <label className="flex items-center gap-1.5 font-semibold cursor-pointer">
      <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} data-testid="prospect-map-toggle" /> Prospects ({prospects.length} localisés)
    </label>
    {Object.entries(STATUS_COLORS).map(([k, v]) => (
      <span key={k} className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: v.c }} /> {v.l} ({prospects.filter((p) => p.status === k).length})</span>
    ))}
    <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full border-2 border-brand bg-white" /> Ville ciblée</span>
    <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full border-2 border-dashed border-red-600 bg-white" /> Zone blanche (aucun contact)</span>
  </div>
);

const ProspectMap = ({ siege, radiusKm, villes, selected, onToggle }) => {
  const [prospects, setProspects] = useState([]);
  const [show, setShow] = useState(true);
  useEffect(() => {
    getGeoProspects().then((r) => setProspects(r.data.prospects)).catch(() => setProspects([]));
  }, []);
  if (!siege) return null;
  const center = [siege.lat, siege.lng];
  const isOn = (nom) => selected.some((v) => v.toLowerCase() === nom.toLowerCase());
  return (
    <>
      <div className="rounded-lg overflow-hidden border border-neutral-200 h-72 mb-2" data-testid="prospect-map">
        <MapContainer center={center} zoom={9} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitRadius center={center} radiusKm={radiusKm} />
          <Circle center={center} radius={radiusKm * 1000} pathOptions={{ color: "#1e50ff", fillColor: "#1e50ff", fillOpacity: 0.08, weight: 2 }} />
          <Marker position={center} icon={pin}><Tooltip>Siège — rayon {radiusKm} km</Tooltip></Marker>
          {villes.map((v) => {
            const on = isOn(v.nom);
            return (
              <CircleMarker key={v.code} center={[v.lat, v.lng]} radius={on ? 7 : v.zone_blanche ? 6 : 4} eventHandlers={{ click: () => onToggle(v.nom) }}
                pathOptions={{ color: v.zone_blanche ? "#dc2626" : "#1e50ff", dashArray: v.zone_blanche ? "3 3" : null, fillColor: on ? "#1e50ff" : "#fff", fillOpacity: 1, weight: 2 }}>
                <Tooltip>{v.nom} · {v.distance_km} km · {new Intl.NumberFormat("fr-FR").format(v.population)} hab.{on ? " · sélectionnée" : ""}{v.zone_blanche ? " · ZONE BLANCHE : aucun prospect contacté" : ` · ${v.contacted} contacté(s)`}</Tooltip>
              </CircleMarker>
            );
          })}
          {show && <ProspectDots prospects={prospects} />}
        </MapContainer>
      </div>
      <Legend prospects={prospects} show={show} setShow={setShow} />
    </>
  );
};

export default ProspectMap;
